import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { LogOut, Search, RefreshCw, Download, X, FileText, Mail, Phone, Globe2, Clock } from "lucide-react";

const STATUS = [
  { key: "new", label: "New" },
  { key: "in_review", label: "In Review" },
  { key: "contacted", label: "Contacted" },
  { key: "closed", label: "Closed" },
];

export default function AdminDashboard() {
  const { isAuthed, email, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterKind, setFilterKind] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/consultations", { params: { status: filterStatus || undefined, kind: filterKind || undefined, q: q || undefined } }),
      ]);
      setStats(s.data);
      setItems(list.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        nav("/admin/login", { replace: true });
      } else {
        toast.error("Failed to load");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAuthed) loadAll(); /* eslint-disable-next-line */ }, [isAuthed, filterStatus, filterKind]);

  if (!isAuthed) return <Navigate to="/admin/login" replace />;

  const doSearch = (e) => { e.preventDefault(); loadAll(); };

  const openDetail = async (cid) => {
    try {
      const res = await api.get(`/admin/consultations/${cid}`);
      setSelected(res.data);
    } catch {
      toast.error("Failed to load details");
    }
  };

  const updateStatus = async (cid, status) => {
    try {
      const res = await api.patch(`/admin/consultations/${cid}`, { status });
      setItems((cur) => cur.map((it) => it.id === cid ? res.data : it));
      if (selected) setSelected({ ...selected, consultation: res.data });
      toast.success("Status updated");
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch {
      toast.error("Failed to update");
    }
  };

  const saveNotes = async (cid, notes) => {
    try {
      const res = await api.patch(`/admin/consultations/${cid}`, { notes });
      setItems((cur) => cur.map((it) => it.id === cid ? res.data : it));
      if (selected) setSelected({ ...selected, consultation: res.data });
      toast.success("Notes saved");
    } catch {
      toast.error("Failed to save");
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-[#0B1F3A] text-white">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="w-8 h-8 rounded-full border border-[#C8A96A] flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-[#C8A96A]" />
            </span>
            <span className="font-serif text-[20px] tracking-tight">Easy<span className="text-[#C8A96A]">ilaaz</span> · Concierge Desk</span>
          </div>
          <div className="flex items-center gap-6">
            <span data-testid="admin-email-display" className="text-[12px] text-white/70 hidden sm:block">{email}</span>
            <button data-testid="admin-logout-btn" onClick={() => { logout(); nav("/admin/login"); }} className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-white/80 hover:text-[#C8A96A] transition-colors">
              <LogOut size={14} strokeWidth={1.25}/> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10">
        {/* Stats */}
        <section data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <StatCard label="Total" value={stats?.total ?? "—"} testId="stat-total"/>
          <StatCard label="Last 7 days" value={stats?.last_7_days ?? "—"} testId="stat-recent"/>
          <StatCard label="Consultation" value={stats?.by_kind?.consultation ?? "—"} testId="stat-consult"/>
          <StatCard label="Second Opinion" value={stats?.by_kind?.second_opinion ?? "—"} testId="stat-second"/>
          <StatCard label="New" value={stats?.by_status?.new ?? "—"} testId="stat-new"/>
          <StatCard label="In Review" value={stats?.by_status?.in_review ?? "—"} testId="stat-review"/>
          <StatCard label="Contacted" value={stats?.by_status?.contacted ?? "—"} testId="stat-contacted"/>
        </section>

        {/* Filters */}
        <form onSubmit={doSearch} className="mt-8 flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1">
            <div className="overline text-[#0B1F3A]/50 mb-2">Search</div>
            <div className="relative">
              <Search size={16} strokeWidth={1.25} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0B1F3A]/40" />
              <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email, country or specialty…" className="pl-9" />
            </div>
          </div>
          <div className="md:w-48">
            <div className="overline text-[#0B1F3A]/50 mb-2">Status</div>
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="admin-filter-status"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:w-48">
            <div className="overline text-[#0B1F3A]/50 mb-2">Kind</div>
            <Select value={filterKind || "all"} onValueChange={(v) => setFilterKind(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="admin-filter-kind"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All kinds</SelectItem>
                <SelectItem value="consultation">Consultation</SelectItem>
                <SelectItem value="second_opinion">Second Opinion</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <button data-testid="admin-refresh-btn" type="submit" className="inline-flex items-center justify-center gap-2 h-11 px-5 bg-[#0B1F3A] text-white text-[12px] tracking-[0.16em] uppercase hover:bg-[#C8A96A] transition-colors duration-200">
            <RefreshCw size={14} strokeWidth={1.25} /> Apply
          </button>
        </form>

        {/* Leads table */}
        <section className="mt-8 bg-white border border-[#E6E9EF]">
          <div className="overflow-x-auto">
            <table data-testid="admin-leads-table" className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[#0B1F3A]/50 bg-[#F5F7FA]">
                  <Th>Guest</Th>
                  <Th>Country</Th>
                  <Th>Specialty</Th>
                  <Th>Kind</Th>
                  <Th>Received</Th>
                  <Th>Status</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="p-8 text-center text-[#0B1F3A]/40">Loading…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-[#0B1F3A]/40">No leads match.</td></tr>
                )}
                {!loading && items.map((it) => (
                  <tr key={it.id} data-testid={`admin-lead-row-${it.id}`} className="border-t border-[#E6E9EF] hover:bg-[#F5F7FA]/50 transition-colors">
                    <Td>
                      <div className="font-medium text-[#0B1F3A]">{it.full_name}</div>
                      <div className="text-[11px] text-[#0B1F3A]/50">{it.email}</div>
                    </Td>
                    <Td>{it.country}</Td>
                    <Td>{it.specialty}</Td>
                    <Td><KindBadge kind={it.kind} /></Td>
                    <Td>{formatDate(it.created_at)}</Td>
                    <Td>
                      <Select value={it.status} onValueChange={(v) => updateStatus(it.id, v)}>
                        <SelectTrigger data-testid={`admin-status-select-${it.id}`} className="w-36 h-9 text-[12px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Td>
                    <Td>
                      <button
                        data-testid={`admin-open-lead-${it.id}`}
                        onClick={() => openDetail(it.id)}
                        className="text-[11px] tracking-[0.16em] uppercase text-[#0B1F3A] link-elegant"
                      >
                        View
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {selected && (
        <LeadDrawer
          data={selected}
          onClose={() => setSelected(null)}
          onSaveNotes={(notes) => saveNotes(selected.consultation.id, notes)}
          onStatus={(status) => updateStatus(selected.consultation.id, status)}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-[#E6E9EF] p-5">
      <div className="overline text-[#0B1F3A]/50">{label}</div>
      <div className="mt-2 font-serif text-4xl text-[#0B1F3A]">{value}</div>
    </div>
  );
}

function Th({ children }) { return <th className="px-5 py-4 text-[11px] tracking-[0.14em] uppercase font-medium">{children}</th>; }
function Td({ children }) { return <td className="px-5 py-4 align-top">{children}</td>; }

function KindBadge({ kind }) {
  const k = kind || "consultation";
  const map = {
    consultation: { label: "Consultation", cls: "bg-[#0B1F3A]/8 text-[#0B1F3A]" },
    second_opinion: { label: "2nd Opinion", cls: "bg-[#C8A96A]/15 text-[#0B1F3A] border border-[#C8A96A]/40" },
  }[k] || { label: k, cls: "bg-[#0B1F3A]/8 text-[#0B1F3A]" };
  return <span className={`inline-block text-[10px] tracking-[0.14em] uppercase px-2.5 py-1 ${map.cls}`}>{map.label}</span>;
}

function formatDate(s) {
  try { return new Date(s).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
}

function LeadDrawer({ data, onClose, onSaveNotes, onStatus }) {
  const { consultation: c, files } = data;
  const [notes, setNotes] = useState(c.notes || "");
  const token = useMemo(() => localStorage.getItem("ez_admin_token"), []);

  const downloadFile = async (fid, filename) => {
    try {
      const res = await fetch(`${API}/admin/files/${fid}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename || fid; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admin-lead-drawer">
      <div className="absolute inset-0 bg-[#0B1F3A]/50" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-2xl h-full bg-white overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[#E6E9EF] px-8 py-5 flex items-center justify-between z-10">
          <div>
            <div className="overline text-[#C8A96A]">{c.kind === "second_opinion" ? "Second Opinion" : "Consultation"}</div>
            <div className="font-serif text-2xl text-[#0B1F3A] mt-1">{c.full_name}</div>
          </div>
          <button onClick={onClose} data-testid="admin-drawer-close" className="text-[#0B1F3A]/60 hover:text-[#0B1F3A]"><X strokeWidth={1.25} /></button>
        </div>

        <div className="p-8 space-y-8">
          <section className="grid grid-cols-2 gap-y-5">
            <Info icon={Mail} label="Email" value={c.email} />
            <Info icon={Phone} label="Phone" value={c.phone} />
            <Info icon={Globe2} label="Country" value={c.country} />
            <Info icon={Clock} label="Received" value={formatDate(c.created_at)} />
            <Info label="Specialty" value={c.specialty} />
            <Info label="Preferred contact" value={c.preferred_contact} />
            <Info label="Patient age" value={c.patient_age || "—"} />
            <Info label="Urgency" value={c.urgency} />
          </section>

          {c.message && (
            <section>
              <div className="overline text-[#0B1F3A]/50 mb-2">Guest's note</div>
              <p className="text-[14px] leading-relaxed text-[#0B1F3A]/80 bg-[#F5F7FA] p-5 border border-[#E6E9EF]">{c.message}</p>
            </section>
          )}

          <section>
            <div className="overline text-[#0B1F3A]/50 mb-3">Medical reports ({files?.length || 0})</div>
            {files?.length ? (
              <ul className="space-y-2">
                {files.map((f) => (
                  <li key={f.id} data-testid={`admin-file-${f.id}`} className="flex items-center gap-3 bg-[#F5F7FA] px-4 py-3 border border-[#E6E9EF]">
                    <FileText size={16} strokeWidth={1.25} className="text-[#C8A96A]"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-[#0B1F3A] truncate">{f.original_filename}</div>
                      <div className="text-[11px] text-[#0B1F3A]/50">{(f.size / 1024).toFixed(1)} KB · {f.content_type}</div>
                    </div>
                    <button
                      data-testid={`admin-file-download-${f.id}`}
                      onClick={() => downloadFile(f.id, f.original_filename)}
                      className="inline-flex items-center gap-2 h-9 px-3 border border-[#0B1F3A] text-[11px] tracking-[0.14em] uppercase text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white transition-colors duration-200"
                    >
                      <Download size={14} strokeWidth={1.25}/> Download
                    </button>
                  </li>
                ))}
              </ul>
            ) : <p className="text-[13px] text-[#0B1F3A]/50">No reports attached.</p>}
          </section>

          <section>
            <div className="overline text-[#0B1F3A]/50 mb-3">Case status</div>
            <div className="flex gap-2 flex-wrap">
              {STATUS.map((s) => (
                <button
                  key={s.key}
                  data-testid={`admin-drawer-status-${s.key}`}
                  onClick={() => onStatus(s.key)}
                  className={`px-4 h-9 border text-[11px] tracking-[0.14em] uppercase transition-colors duration-200 ${
                    c.status === s.key ? "bg-[#0B1F3A] border-[#0B1F3A] text-white" : "border-[#E6E9EF] text-[#0B1F3A] hover:border-[#0B1F3A]"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </section>

          <section>
            <div className="overline text-[#0B1F3A]/50 mb-3">Concierge notes</div>
            <Textarea data-testid="admin-drawer-notes" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Private notes for the concierge team…" />
            <button
              data-testid="admin-drawer-save-notes"
              onClick={() => onSaveNotes(notes)}
              className="mt-3 inline-flex items-center h-10 px-5 bg-[#0B1F3A] text-white text-[11px] tracking-[0.16em] uppercase hover:bg-[#C8A96A] transition-colors duration-200"
            >
              Save notes
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-2 overline text-[#0B1F3A]/50">
        {Icon && <Icon size={12} strokeWidth={1.5}/>}{label}
      </div>
      <div className="mt-1 text-[14px] text-[#0B1F3A]">{value}</div>
    </div>
  );
}
